import { getApiBaseUrl, getTenantSlugFromHostname } from './api';

export const CLAIM_STATUSES = {
  UNCLAIMED: 'unclaimed',
  CLAIM_STARTED: 'claim_started',
  VERIFICATION_SENT: 'verification_sent',
  VERIFICATION_FAILED: 'verification_failed',
  PENDING_REVIEW: 'pending_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CLAIMED: 'claimed',
  OWNERSHIP_DISPUTED: 'ownership_disputed',
};

export const PUBLISH_STATUSES = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
  PENDING_REVIEW: 'pending_review',
  NEEDS_CHANGES: 'needs_changes',
  SUSPENDED: 'suspended',
  ARCHIVED: 'archived',
};

function normalizeListingId(rawId) {
  return String(rawId || '').trim().toLowerCase();
}

function buildHeaders(extra = {}, { omitTenantHeader = false } = {}) {
  const headers = {
    ...extra,
  };

  const tenantSlug = getTenantSlugFromHostname();
  if (tenantSlug && !omitTenantHeader) {
    headers['X-Tenant-Slug'] = tenantSlug;
  }

  return headers;
}

async function requestJson(path, options = {}) {
  const baseUrl = getApiBaseUrl();
  const method = String(options.method || 'GET').toUpperCase();
  const shouldSendJsonContentType = method !== 'GET' && method !== 'HEAD' && options.body !== undefined;
  const timeoutMs = 15000;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  console.info('[claimFlow.requestJson] start', {
    method,
    path,
    url: `${baseUrl}${path}`,
    hasBody: options.body !== undefined,
    timeoutMs,
  });

  const response = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    ...options,
    signal: controller.signal,
    headers: buildHeaders({
      ...(shouldSendJsonContentType ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    }, { omitTenantHeader: options.omitTenantHeader === true }),
  }).finally(() => {
    window.clearTimeout(timeoutId);
  }).catch((error) => {
    if (error?.name === 'AbortError') {
      const timeoutError = new Error(`Request timeout after ${timeoutMs}ms: ${method} ${path}`);
      timeoutError.code = 'REQUEST_TIMEOUT';
      throw timeoutError;
    }

    throw error;
  });

  const payload = await response.json().catch(() => null);
  console.info('[claimFlow.requestJson] response', {
    method,
    path,
    status: response.status,
    ok: response.ok,
    payload,
  });

  if (!response.ok) {
    const error = new Error(payload?.message || payload?.error || `HTTP ${response.status}`);
    error.code = payload?.code || null;
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

async function requestMultipart(path, formData, options = {}) {
  const baseUrl = getApiBaseUrl();
  const method = String(options.method || 'POST').toUpperCase();
  const timeoutMs = 15000;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  console.info('[claimFlow.requestMultipart] start', {
    method,
    path,
    url: `${baseUrl}${path}`,
    timeoutMs,
  });

  const response = await fetch(`${baseUrl}${path}`, {
    credentials: 'include',
    method,
    body: formData,
    signal: controller.signal,
    headers: buildHeaders({
      ...(options.headers || {}),
    }),
  }).finally(() => {
    window.clearTimeout(timeoutId);
  }).catch((error) => {
    if (error?.name === 'AbortError') {
      const timeoutError = new Error(`Request timeout after ${timeoutMs}ms: ${method} ${path}`);
      timeoutError.code = 'REQUEST_TIMEOUT';
      throw timeoutError;
    }

    throw error;
  });

  const payload = await response.json().catch(() => null);
  console.info('[claimFlow.requestMultipart] response', {
    method,
    path,
    status: response.status,
    ok: response.ok,
    payload,
  });

  if (!response.ok) {
    throw new Error(payload?.message || payload?.error || `HTTP ${response.status}`);
  }

  return payload;
}

export function isClaimBlocked(record) {
  if (!record) return false;
  return [
    CLAIM_STATUSES.CLAIMED,
    CLAIM_STATUSES.PENDING_REVIEW,
    CLAIM_STATUSES.OWNERSHIP_DISPUTED,
  ].includes(record.status);
}

export async function requestListingClaim(listingId, payload) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/request`, {
    method: 'POST',
    body: JSON.stringify(payload || {}),
  });
}

export async function createManualClaimListing(payload) {
  return requestJson('/api/claims/listings/manual', { method: 'POST', body: JSON.stringify(payload) });
}

export async function approveAutomatedClaim(listingId, claimToken) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/automation/approve`, {
    method: 'POST',
    body: JSON.stringify(typeof claimToken === 'object' ? claimToken : { claimToken }),
  });
}

export async function reserveFreeClaimPromotion(listingId) {
  const code = sessionStorage.getItem('mtsPromotionCode');
  if (!code) return null;
  const promotion = await requestJson(`/api/public/promotions/${encodeURIComponent(code)}`);
  if (!promotion?.canRedeem || promotion?.eligibleOffer?.finalAmount !== 0) return null;
  const offer = promotion.eligibleOffer || {};
  const reservation = await requestJson(`/api/public/promotions/${encodeURIComponent(code)}/reserve`, {
    method: 'POST',
    body: JSON.stringify({ flow: 'CLAIM_LISTING', listingId, ...(offer.productId ? { productId: offer.productId } : {}), ...(offer.priceId ? { priceId: offer.priceId } : {}), ...(offer.listingPlanId ? { listingPlanId: offer.listingPlanId } : {}) }),
  });
  return { promotionRedemptionId: reservation.redemptionId, promotionReservationToken: reservation.reservationToken };
}

export async function requestClaimVerificationCall(listingId, claimToken) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/automation/call-request`, {
    method: 'POST',
    body: JSON.stringify({ claimToken }),
  });
}

export async function fetchClaimPhoneOptions(listingId, claimToken) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/automation/phone-options?claimToken=${encodeURIComponent(claimToken)}`);
}

export async function requestClaimPhoneCode(listingId, payload) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/automation/phone-verification/request`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function verifyClaimPhoneCode(listingId, payload) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/automation/phone-verification/verify`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function continueClaimManualReview(listingId, claimToken) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/automation/manual-review`, {
    method: 'POST',
    body: JSON.stringify({ claimToken }),
  });
}

export async function autocompleteClaimGooglePlaces(input, sessionToken) {
  return requestJson('/api/claims/google/autocomplete', {
    method: 'POST',
    body: JSON.stringify({ input, sessionToken }),
    // Tenant identity must come from the hostname resolved by the API.
    omitTenantHeader: true,
  });
}

export async function importClaimListingFromGooglePlace(googlePlaceId, sessionToken = null, categories = {}) {
  const normalizedPlaceId = String(googlePlaceId || '').trim();
  if (!normalizedPlaceId) {
    throw new Error('googlePlaceId is required');
  }

  return requestJson('/api/claims/google/import', {
    method: 'POST',
    body: JSON.stringify({ googlePlaceId: normalizedPlaceId, sessionToken, primaryCategoryId: categories.primaryCategoryId || undefined, secondaryCategoryId: categories.secondaryCategoryId || undefined }),
    omitTenantHeader: true,
  });
}

export async function suggestClaimGooglePlaceCategory(googlePlaceId, sessionToken = null) {
  return requestJson('/api/claims/google/category-suggestion', {
    method: 'POST', body: JSON.stringify({ googlePlaceId, sessionToken }), omitTenantHeader: true,
  });
}

export async function getOwnerWorkspace(listingId) {
  const normalizedId = normalizeListingId(listingId);
  const path = `/api/claims/listings/${encodeURIComponent(normalizedId)}/workspace`;
  console.info('[claimFlow.getOwnerWorkspace] request', {
    listingId: normalizedId,
    path,
    method: 'GET',
  });

  try {
    const payload = await requestJson(path, {
      method: 'GET',
    });

    console.info('[claimFlow.getOwnerWorkspace] response', {
      listingId: normalizedId,
      hasWorkspace: Boolean(payload?.workspace),
      workspaceKeys: payload?.workspace ? Object.keys(payload.workspace) : [],
      payload,
    });

    return payload.workspace;
  } catch (error) {
    console.error('[claimFlow.getOwnerWorkspace] error', {
      listingId: normalizedId,
      path,
      message: error?.message || 'Unknown error',
      error,
    });
    throw error;
  }
}

export async function upsertOwnerWorkspace(listingId, patch) {
  const normalizedId = normalizeListingId(listingId);
  const path = `/api/claims/listings/${encodeURIComponent(normalizedId)}/workspace`;
  const requestBody = patch || {};

  console.info('[claimFlow.upsertOwnerWorkspace] request', {
    listingId: normalizedId,
    path,
    method: 'PATCH',
    patch: requestBody,
  });

  try {
    const payload = await requestJson(path, {
      method: 'PATCH',
      body: JSON.stringify(requestBody),
    });

    console.info('[claimFlow.upsertOwnerWorkspace] response', {
      listingId: normalizedId,
      hasWorkspace: Boolean(payload?.workspace),
      workspaceKeys: payload?.workspace ? Object.keys(payload.workspace) : [],
      payload,
    });

    return payload.workspace;
  } catch (error) {
    console.error('[claimFlow.upsertOwnerWorkspace] error', {
      listingId: normalizedId,
      path,
      message: error?.message || 'Unknown error',
      error,
    });
    throw error;
  }
}

export async function fetchGoogleSummary(listingId) {
  const normalizedId = normalizeListingId(listingId);
  try {
    const payload = await requestJson(
      `/api/claims/listings/${encodeURIComponent(normalizedId)}/google-summary`,
      { method: 'GET' }
    );
    return typeof payload?.summary === 'string' ? payload.summary : null;
  } catch {
    return null;
  }
}

export async function uploadListingLogoAsset(listingId, file, options = {}) {
  const normalizedId = normalizeListingId(listingId);
  const formData = new FormData();

  formData.append('file', file);
  formData.append('ownerType', String(options.ownerType || 'BUSINESS').toUpperCase());
  formData.append('ownerId', String(options.ownerId || normalizedId));
  formData.append('collectionKey', String(options.collectionKey || 'listing.logo'));

  if (options.visibility) {
    formData.append('visibility', String(options.visibility).toUpperCase());
  }

  if (Array.isArray(options.tags) && options.tags.length > 0) {
    formData.append('tags', JSON.stringify(options.tags));
  }

  if (options.altText) {
    formData.append('altText', String(options.altText));
  }

  if (options.metadataJson && typeof options.metadataJson === 'object') {
    formData.append('metadataJson', JSON.stringify(options.metadataJson));
  }

  return requestMultipart('/api/media/assets', formData, {
    method: 'POST',
  });
}

export async function getOwnerListingDraft(listingId) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/draft`, {
    method: 'GET',
  });
}

export async function upsertOwnerListingDraft(listingId, payload) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/draft`, {
    method: 'PATCH',
    body: JSON.stringify(payload || {}),
  });
}

export async function submitOwnerListingDraft(listingId) {
  const normalizedId = normalizeListingId(listingId);
  return requestJson(`/api/claims/listings/${encodeURIComponent(normalizedId)}/draft/submit`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
